import React from "react";
import { ChevronDown } from "lucide-react";

import {
  derivedOpeningCosts,
  editHistoryRow,
  historyRowState,
  openingBatchState,
  openingCostGap,
  setHistoryDeleted,
  setOpeningBatch,
  setOpeningBatchDeleted,
  undoOpeningBatch,
} from "./holdingLedgerDraftModel";
import styles from "./holdingEditDialog.module.css";

// 「记录交易」弹窗的历史区：两个 tab 下都全量展示，可改、可删（spec §4.1.1）。
// 删除只是打标记，提交前可以撤销；真正删不删由整本账提交决定。
// 录入持仓可以有多批（2026-09-16），已提交的每批在这里逐条改、删；
// 「录入持仓」tab 只管新增一批，不再和这里共用一份数据。
// 历史区只显示已提交的记录（用户 2026-09-15）：
//   - 没提交过的录入持仓、新增交易不进历史区；
//   - 文字永远是库里的值；改了没提交的行保持展开成输入框，「收起」换成「撤销修改」，
//     否则收起后显示旧值看不出改过、显示新值又像已经生效。

function displayNumber(value, maximumFractionDigits = 4) {
  if (value === null || value === undefined || value === "") return "--";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toLocaleString("zh-CN", { maximumFractionDigits });
}

const isSell = (action) => ["SELL", "REDEEM"].includes(String(action || "").toUpperCase());
// 只标「待删除」：「已修改」会让人以为不提交就已经改了（用户 2026-09-15 要求删掉）
const TAG = { deleted: "待删除" };

// 编辑行不显示字段名：多一行字段名就让整行比只读行高 19px。四个框自己说得清——
// 日期是 date 控件、方向是买入/卖出下拉、价格和股数靠框内的单位后缀（与上方
// 「录入交易记录」区同一套写法）。字段名仍在每个 input 的 aria-label 上。
function Cell({ suffix, children }) {
  return <div className={styles.historyCell}>{children}{suffix && <small>{suffix}</small>}</div>;
}

export function HoldingLedgerHistory({ draft, onChange, disabled, isOpenFund, unit = "股", issues, navState }) {
  const [open, setOpen] = React.useState(true);
  const [editing, setEditing] = React.useState(() => new Set());
  const toggleEditing = (key) => setEditing((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const rows = [
    ...draft.history.map((row) => {
      const values = { ...row.stored, ...row.edits };
      return { key: row.key, kind: "trade", date: row.stored.trade_date || "", row, values, stored: row.stored };
    }),
    ...draft.openings.map((batch) => ({
      key: batch.key, kind: "opening", date: batch.stored.effective_date || "", batch, values: batch.values, stored: batch.stored,
    })),
  ].sort((left, right) => String(right.date).localeCompare(String(left.date)));
  // 只有一批时读屏名称仍叫「录入持仓」（和多批之前一样）；多批时按页面上的先后编号
  const openingLabels = new Map(
    rows.filter((row) => row.kind === "opening")
      .map((row, index, all) => [row.key, all.length > 1 ? `录入持仓第 ${index + 1} 条` : "录入持仓"]),
  );

  return (
    <section className={styles.history}>
      <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <strong>历史记录</strong><ChevronDown size={17} className={open ? styles.expanded : ""} />
      </button>
      {open && (rows.length === 0 ? <div className={styles.historyEmpty}>暂无历史记录</div> : (
        <ul className={styles.historyList}>
          {rows.map((item, position) => {
            const state = item.kind === "trade" ? historyRowState(item.row) : openingBatchState(item.batch);
            const issue = issues?.get(item.key);
            const issueId = issue ? `ledger-issue-${position}` : undefined;
            // 只标和问题相关的输入框（走查 W-02）
            const flagged = (field) => (issue?.fields.has(field) ? "true" : undefined);
            const describedBy = (field) => (flagged(field) ? issueId : undefined);
            const isEditing = (editing.has(item.key) || state === "edited") && state !== "deleted";
            const nav = item.kind === "trade" && state !== "deleted" ? navState?.[item.key] : undefined;
            const label = item.kind === "trade" ? `历史第 ${position + 1} 条` : openingLabels.get(item.key);
            const patch = (fields) => onChange((current) => (
              item.kind === "trade" ? editHistoryRow(current, item.key, fields) : setOpeningBatch(current, item.key, fields)
            ));
            const setDeleted = (deleted) => onChange((current) => (
              item.kind === "trade"
                ? setHistoryDeleted(current, item.key, deleted)
                : setOpeningBatchDeleted(current, item.key, deleted)
            ));
            const undoEdits = () => {
              onChange((current) => (
                item.kind === "trade"
                  ? editHistoryRow(current, item.key, Object.fromEntries(
                    Object.keys(item.row.edits).map((field) => [field, item.stored[field]]),
                  ))
                  : undoOpeningBatch(current, item.key)
              ));
              setEditing((current) => { const next = new Set(current); next.delete(item.key); return next; });
            };
            const { values, stored } = item;
            return (
              <li
                key={item.key}
                className={[styles.historyRow, state === "deleted" ? styles.deletedRow : "",
                  issue ? styles.issueRow : "", isEditing ? styles.editingRow : ""].join(" ")}
                data-row-key={item.key}
              >
                <div className={styles.historyMain}>
                  <span className={styles.historyDate}>{item.date || "日期待补充"}</span>
                  <span className={styles.historyKind}>{item.kind === "trade" ? "交易记录" : "录入持仓"}</span>
                  {isEditing ? (
                    <div className={styles.historyEditGrid}>
                      {item.kind === "trade" ? (
                        <>
                          <Cell><input type="date" aria-label={`${label}日期`} value={values.trade_date || ""} disabled={disabled} aria-invalid={flagged("trade_date")} aria-describedby={describedBy("trade_date")} onChange={(event) => patch({ trade_date: event.target.value })} /></Cell>
                          <Cell><select aria-label={`${label}买卖方向`} value={isSell(values.action) ? "SELL" : "BUY"} disabled={disabled} onChange={(event) => patch({ action: event.target.value })}><option value="BUY">买入</option><option value="SELL">卖出</option></select></Cell>
                          <Cell suffix={unit}><input inputMode="numeric" aria-label={`${label}股数`} value={values.quantity ?? ""} disabled={disabled} aria-invalid={flagged("quantity")} aria-describedby={describedBy("quantity")} onChange={(event) => patch({ quantity: event.target.value })} /></Cell>
                          <Cell suffix="元"><input inputMode="decimal" aria-label={`${label}价格`} value={values.price ?? ""} placeholder={nav?.status === "loading" ? "正在获取净值…" : undefined} readOnly={isOpenFund} disabled={disabled} aria-invalid={flagged("price") || (nav?.status === "error" ? "true" : undefined)} aria-describedby={describedBy("price")} onChange={(event) => patch({ price: event.target.value })} /></Cell>
                        </>
                      ) : (
                        <>
                          <Cell><input type="date" aria-label="录入持仓日期" value={values.effective_date || ""} disabled={disabled} onChange={(event) => patch({ effective_date: event.target.value })} /></Cell>
                          <Cell suffix={unit}><input inputMode="numeric" aria-label="录入持仓数量" value={values.quantity ?? ""} disabled={disabled} aria-invalid={issue ? "true" : undefined} aria-describedby={issueId} onChange={(event) => patch({ quantity: event.target.value })} /></Cell>
                          <Cell suffix={`元 / ${unit}`}><input inputMode="decimal" aria-label="录入持仓成本" placeholder={item.kind === "opening" ? (derivedOpeningCosts(values).cost_price ?? undefined) : undefined} value={values.cost_price ?? ""} disabled={disabled} onChange={(event) => patch({ cost_price: event.target.value })} /></Cell>
                          <Cell suffix="元"><input inputMode="decimal" aria-label="录入持仓总成本" placeholder={derivedOpeningCosts(values).total_cost ?? undefined} value={values.total_cost ?? ""} disabled={disabled} onChange={(event) => patch({ total_cost: event.target.value })} /></Cell>
                        </>
                      )}
                    </div>
                  ) : (
                    <span className={styles.historyText}>
                      {item.kind === "trade"
                        ? `${isSell(stored.action) ? "卖出" : "买入"} ${displayNumber(stored.quantity)} ${unit}，价格 ${displayNumber(stored.price)} 元`
                        : `建仓 ${displayNumber(stored.quantity)} ${unit}，成本价 ${displayNumber(stored.cost_price, 4)} 元`}
                    </span>
                  )}
                  {TAG[state] && <span className={styles.rowTag} data-state={state}>{TAG[state]}</span>}
                  <span className={styles.historyActions}>
                    {state === "deleted" ? (
                      <button type="button" disabled={disabled} onClick={() => setDeleted(false)} aria-label={`撤销删除${label}`}>撤销删除</button>
                    ) : (
                      <>
                        {state === "edited" ? (
                          <button type="button" disabled={disabled} onClick={undoEdits} aria-label={`撤销修改${label}`}>撤销修改</button>
                        ) : (
                          <button type="button" disabled={disabled} onClick={() => toggleEditing(item.key)} aria-label={`${isEditing ? "收起" : "修改"}${label}`}>{isEditing ? "收起" : "修改"}</button>
                        )}
                        <button type="button" disabled={disabled} onClick={() => setDeleted(true)} aria-label={`删除${label}`}>删除</button>
                      </>
                    )}
                  </span>
                </div>
                {nav?.error && <p className={styles.issueText} role="alert">{nav.error}</p>}
                {item.kind === "opening" && isEditing && openingCostGap(values) && (
                  <p className={styles.costHint} role="status">
                    总成本和「持仓数量 × 持仓成本」（{Number(openingCostGap(values).expected).toLocaleString("zh-CN", { minimumFractionDigits: 2 })} 元）相差较大，请确认是否填错。
                  </p>
                )}
                {issue && <p className={styles.issueText} id={issueId} role="alert">{issue.text}</p>}
              </li>
            );
          })}
        </ul>
      ))}
    </section>
  );
}
